import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { library } from '@cwos/api-client';
import { Button, Pill } from '@cwos/ui-admin';
import { useNav } from '../navigation.js';
import { LoadFailure } from '../LoadFailure.js';
import { DraftedBy } from './PhibianDrafts.js';
import {
  bindingnessLabel,
  bindingnessSentence,
  bindingnessVariant,
  extractionKindLabel,
  formatInstantDay,
  ruleLevelLabel,
  ruleLevelSentence,
} from './libraryLogic.js';

/**
 * THE HUMAN APPROVAL QUEUE'S DECISION STEP.
 *
 * The coverage audit's note on O4 is the reason this exists:
 *
 *   "this is the human-approval queue for extracted policy. It is the one place
 *    the product asks a person to approve what the model may treat as
 *    authoritative, and it is unreachable."
 *
 * An approval queue nobody can reach means the approval step is not happening.
 * The queue itself became reachable when the Library got a door; this is the
 * part that lets a person actually decide.
 *
 * ===========================================================================
 * WHICH GATE THIS IS RENDERED AGAINST
 * ===========================================================================
 * `DOCTRINE_APPROVAL_PERMISSION` — TODAY `knowledge:publish`. That is the LIVE
 * gate on `POST /knowledge/extractions/:id/{approve,reject}` and on the queue
 * read, so it is the gate this screen renders against: a control shown against
 * a key the route does not check is a control that 403s.
 *
 * `knowledge:doctrine:approve` is DECLARED beside it
 * (`PROPOSED_DOCTRINE_APPROVAL_PERMISSION`) and is NOT the live gate — nothing
 * seeds it, and `can()` against an unseeded key is a denial, so wiring it in
 * would lock out every existing administrator. The overreach is real and worth
 * saying out loud rather than papering over: `knowledge:publish` means "make an
 * article version the thing the organization reads", and approving an
 * extraction makes a reading of a document into an operational rule that steers
 * scheduling, eligibility and supervision answers. An administrator granting
 * "publish articles" today does not think they are granting "approve doctrine".
 * When the key is seeded, the swap is one edit in `doctrine/routes.ts` and one
 * constant here.
 *
 * ===========================================================================
 * THREE THINGS THIS FORM SAYS OUT LOUD
 * ===========================================================================
 *
 * 1. THE START DATE IS REQUIRED AND HAS NO DEFAULT. A rule that quietly took
 *    effect at the moment of the click is a rule whose start date was decided
 *    by timing, and every conclusion drawn from it later would cite that
 *    accident as its authority. The field starts empty and the button stays
 *    disabled.
 *
 * 2. AN APPROVER MAY NOT REWRITE THE SENTENCE. Classification and applicability
 *    are re-decidable; the statement is copied verbatim. A reviewer who
 *    disagrees with the wording rejects and re-proposes — because a rule whose
 *    text one person wrote and another silently amended has two authors and one
 *    signature. So the statement is shown, prominently, and is not an input.
 *
 * 3. OMITTING THE NARROWING MEANS EVERYWHERE. The approve route builds the
 *    applicability arrays from the request and NOT from the proposal, so an
 *    omitted array is empty and an empty array is the widest possible answer.
 *    This screen offers the two dimensions that are shared text vocabulary —
 *    state or region, and payer — and states in words that leaving them empty
 *    means the rule applies to the whole organization. The uuid dimensions
 *    (programs, services, sites, roles, people) are deliberately NOT offered:
 *    a form that asked an operator to paste participant identifiers would be
 *    showing them their own primary keys and calling it applicability.
 */

/** The key the approve/reject routes actually check today. */
export const DOCTRINE_APPROVAL_KEY = 'knowledge:publish';
/** Declared in `doctrine/routes.ts`, seeded nowhere, guarding nothing yet. */
export const PROPOSED_DOCTRINE_APPROVAL_KEY = 'knowledge:doctrine:approve';

const BINDINGNESS: readonly library.Bindingness[] = [
  'hard_constraint',
  'strong_default',
  'guidance',
  'informational',
];

/**
 * `external_requirement` is absent by construction, not by omission. It is a
 * FLOOR set outside the organization, the wire schema and the storage CHECK
 * both refuse a tenant claiming it, and a select that offered it would be
 * offering an operator a value the server will reject.
 */
const RULE_LEVELS: readonly Exclude<library.RuleLevel, 'external_requirement'>[] = [
  'organization',
  'program',
  'service_type',
  'location',
  'client_specific',
];

export interface ApprovalDecisionProps {
  extraction: library.Extraction;
  onDecided: () => void;
  onCancel: () => void;
}

export function ApprovalDecision({ extraction, onDecided, onCancel }: ApprovalDecisionProps) {
  const navigate = useNav();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'none' | 'approve' | 'reject'>('none');

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['library'] });
  };

  return (
    <section className="rn-card rn-col">
      <h2 className="rn-card-title">A reading waiting for your decision</h2>

      <p className="rn-muted">
        Nothing here is in force. This is what somebody proposed a document says; it becomes a rule
        your organization enforces only when you approve it, and the approval records who and when.
      </p>

      {/*
        UNTRUSTED TEXT. The proposed statement came out of a document somebody
        uploaded, so it is rendered as characters into a paragraph — no markup,
        no interpretation, nothing that could reach `dangerouslySetInnerHTML`.
        Approval establishes that a rule is enforceable; it never establishes
        that its text is safe to obey.
      */}
      <p className="lib-body">{extraction.proposedStatement}</p>
      <DraftedBy extraction={extraction} />

      <p className="rn-muted">
        {extractionKindLabel(extraction.kind)} · answers {extraction.subject} · proposed{' '}
        {formatInstantDay(extraction.proposedAt)}
      </p>
      <p className="rn-row">
        <Pill variant={bindingnessVariant(extraction.proposedBindingness)}>
          Proposed as {bindingnessLabel(extraction.proposedBindingness)}
        </Pill>
      </p>
      <p>
        <Button
          variant="quiet"
          small
          onClick={() => navigate(`/library/sources/${extraction.sourceId}`)}
        >
          Open the document it came from
        </Button>
      </p>

      {mode === 'none' ? (
        <div className="rn-row">
          <Button variant="primary" onClick={() => setMode('approve')}>
            Approve this reading
          </Button>
          <Button variant="destructive" onClick={() => setMode('reject')}>
            Turn it down
          </Button>
          <Button variant="quiet" onClick={onCancel}>
            Back to the queue
          </Button>
        </div>
      ) : null}

      {mode === 'approve' ? (
        <ApproveForm
          extraction={extraction}
          onCancel={() => setMode('none')}
          onDone={() => {
            invalidate();
            onDecided();
          }}
        />
      ) : null}

      {mode === 'reject' ? (
        <RejectForm
          extraction={extraction}
          onCancel={() => setMode('none')}
          onDone={() => {
            invalidate();
            onDecided();
          }}
        />
      ) : null}
    </section>
  );
}

function ApproveForm({
  extraction,
  onCancel,
  onDone,
}: {
  extraction: library.Extraction;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [bindingness, setBindingness] = useState<library.Bindingness>(
    extraction.proposedBindingness,
  );
  const [ruleLevel, setRuleLevel] = useState<Exclude<library.RuleLevel, 'external_requirement'>>(
    extraction.proposedRuleLevel === 'external_requirement'
      ? 'organization'
      : extraction.proposedRuleLevel,
  );
  // NO DEFAULT. See the header, point 1.
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [effectiveTo, setEffectiveTo] = useState('');
  const [jurisdictions, setJurisdictions] = useState('');
  const [payers, setPayers] = useState('');
  const [supersedesId, setSupersedesId] = useState('');
  const [note, setNote] = useState('');

  /**
   * What is already in force on this subject.
   *
   * Supersession is OPT-IN at the API: omitting `supersedesId` leaves BOTH
   * rules active on the subject and the resolver adjudicating between them by
   * rank and recency — a defined outcome that nobody was asked about. So the
   * candidates are put in front of the approver rather than left to be
   * remembered, and the sentence below says what omitting it does.
   */
  const candidates = useQuery({
    queryKey: ['library', 'rules', 'subject', extraction.subject],
    queryFn: () => library.rules({ state: 'active', subject: extraction.subject }),
  });

  const approve = useMutation({
    mutationFn: () =>
      library.approveExtraction(extraction.id, {
        bindingness,
        ruleLevel,
        effectiveFrom,
        ...(effectiveTo === '' ? {} : { effectiveTo }),
        ...(jurisdictions.trim() === '' ? {} : { jurisdictions: splitList(jurisdictions) }),
        ...(payers.trim() === '' ? {} : { payers: splitList(payers) }),
        ...(supersedesId === '' ? {} : { supersedesId }),
        ...(note.trim() === '' ? {} : { note: note.trim() }),
      }),
    onSuccess: onDone,
  });

  const ready = effectiveFrom !== '';
  const replacing = (candidates.data ?? []).find((r) => r.id === supersedesId) ?? null;

  return (
    <form
      className="rn-col"
      onSubmit={(e) => {
        e.preventDefault();
        if (!ready) return;
        approve.mutate();
      }}
    >
      <h3>Approve</h3>
      <p className="rn-muted">
        You are deciding how binding this is and who it applies to. You cannot change its wording —
        the approved rule copies the sentence above exactly. If the wording is wrong, turn it down
        and have it proposed again, so a rule never has two authors and one signature.
      </p>

      <label className="rn-col">
        <span className="rn-strong">How binding</span>
        <select
          value={bindingness}
          aria-label="How binding"
          onChange={(e) => setBindingness(e.target.value as library.Bindingness)}
        >
          {BINDINGNESS.map((b) => (
            <option key={b} value={b}>
              {bindingnessLabel(b)}
            </option>
          ))}
        </select>
        <span className="rn-muted">{bindingnessSentence(bindingness)}</span>
      </label>

      <label className="rn-col">
        <span className="rn-strong">How specific</span>
        <select
          value={ruleLevel}
          aria-label="How specific"
          onChange={(e) =>
            setRuleLevel(e.target.value as Exclude<library.RuleLevel, 'external_requirement'>)
          }
        >
          {RULE_LEVELS.map((l) => (
            <option key={l} value={l}>
              {ruleLevelLabel(l)}
            </option>
          ))}
        </select>
        <span className="rn-muted">{ruleLevelSentence(ruleLevel)}</span>
      </label>

      <label className="rn-col">
        <span className="rn-strong">In force from</span>
        <input
          type="date"
          value={effectiveFrom}
          aria-label="In force from"
          onChange={(e) => setEffectiveFrom(e.target.value)}
        />
        <span className="rn-muted">
          Required, and Phibian will not choose it for you. A rule that quietly took effect at the
          moment of the click is a rule whose start date was decided by timing.
        </span>
      </label>

      <label className="rn-col">
        <span className="rn-strong">Until (optional)</span>
        <input
          type="date"
          value={effectiveTo}
          aria-label="Until"
          onChange={(e) => setEffectiveTo(e.target.value)}
        />
        <span className="rn-muted">Leave empty for a rule with no end date.</span>
      </label>

      <label className="rn-col">
        <span className="rn-strong">Only in these states or regions (optional)</span>
        <input
          value={jurisdictions}
          aria-label="Only in these states or regions"
          onChange={(e) => setJurisdictions(e.target.value)}
        />
      </label>

      <label className="rn-col">
        <span className="rn-strong">Only for these payers (optional)</span>
        <input
          value={payers}
          aria-label="Only for these payers"
          onChange={(e) => setPayers(e.target.value)}
        />
      </label>

      <p className="rn-muted" role="note">
        Leaving both empty makes this rule apply to your whole organization — every program, every
        service, every site, every role and every person. Narrowing a rule to particular programs or
        people is not something this screen can do yet, and that is an absence rather than a
        default: those dimensions are identifiers, and a form that asked you to paste them would be
        showing you your own database.
      </p>

      <label className="rn-col">
        <span className="rn-strong">Does this replace a rule already in force?</span>
        <select
          value={supersedesId}
          aria-label="Does this replace a rule already in force?"
          onChange={(e) => setSupersedesId(e.target.value)}
        >
          <option value="">No — this is a new rule on this question</option>
          {(candidates.data ?? []).map((r) => (
            <option key={r.id} value={r.id}>
              {r.statement}
            </option>
          ))}
        </select>
        <span className="rn-muted">
          {replacing
            ? 'The rule you named stops applying the day before this one starts, and is kept so an earlier decision can still be explained.'
            : (candidates.data ?? []).length === 0
              ? 'Nothing is in force on this question today, so there is nothing to replace.'
              : 'If you leave this as “no”, BOTH rules stay in force on this question and Phibian decides between them by how specific and how recent each is. That is a defined outcome nobody was asked about.'}
        </span>
        {candidates.isError ? (
          <span className="rn-muted">
            Phibian could not read what is already in force on this question. That is “we could not
            tell”, not “nothing is”.
          </span>
        ) : null}
      </label>

      <label className="rn-col">
        <span className="rn-strong">A note for the record (optional)</span>
        <input
          value={note}
          aria-label="A note for the record"
          onChange={(e) => setNote(e.target.value)}
        />
      </label>

      <div className="rn-row">
        <Button type="submit" variant="primary" disabled={!ready || approve.isPending}>
          {approve.isPending ? 'Approving…' : 'Approve and put it in force'}
        </Button>
        <Button variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      {!ready ? (
        <p className="rn-muted">Choose the date this takes effect before approving.</p>
      ) : null}
      {approve.isError &&
      (approve.error as { body?: { code?: unknown } }).body?.code === 'second_reviewer_required' ? (
        // THE TWO-PERSON RULE: the approver proposed this reading and someone
        // else in the organization can approve it. Not a missing permission.
        <p className="rn-muted kl-error" role="alert">
          You proposed this reading, so someone else in your organization must approve it. Nothing
          was put in force.
        </p>
      ) : approve.isError ? (
        <LoadFailure
          error={approve.error}
          message="We couldn’t approve that just now. Nothing was put in force, so nothing changed about what Phibian recommends."
          refusedHeading="You cannot approve readings"
        />
      ) : null}
    </form>
  );
}

function RejectForm({
  extraction,
  onCancel,
  onDone,
}: {
  extraction: library.Extraction;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [note, setNote] = useState('');
  const reject = useMutation({
    mutationFn: () => library.rejectExtraction(extraction.id, note.trim()),
    onSuccess: onDone,
  });
  const ready = note.trim() !== '';

  return (
    <form
      className="rn-col"
      onSubmit={(e) => {
        e.preventDefault();
        if (!ready) return;
        reject.mutate();
      }}
    >
      <h3>Turn it down</h3>
      <p className="rn-muted">
        A refusal nobody explained is not a decision, so the reason is required. It is kept
        permanently beside the document — a history with the disagreements edited out is not a
        history.
      </p>
      <label className="rn-col">
        <span className="rn-strong">Why</span>
        <textarea
          value={note}
          rows={4}
          aria-label="Why"
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
      <div className="rn-row">
        <Button type="submit" variant="destructive" disabled={!ready || reject.isPending}>
          {reject.isPending ? 'Recording…' : 'Turn it down'}
        </Button>
        <Button variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      {!ready ? <p className="rn-muted">Say why before turning this down.</p> : null}
      {reject.isError ? (
        <LoadFailure
          error={reject.error}
          message="We couldn’t record that just now. The reading is still waiting for a decision."
          refusedHeading="You cannot decide readings"
        />
      ) : null}
    </form>
  );
}

/**
 * A comma-separated list into an array, with the empties dropped.
 *
 * `''.split(',')` is `['']`, and an array holding one empty string is not the
 * same as no narrowing: it would narrow the rule to a jurisdiction literally
 * named "", which matches nothing and makes the rule apply nowhere. The caller
 * checks for a blank before calling this; this drops the stragglers a trailing
 * comma leaves behind.
 */
function splitList(value: string): string[] {
  return value
    .split(',')
    .map((v) => v.trim())
    .filter((v) => v !== '');
}
